import { Box, ButtonBase, Chip, CircularProgress, Stack, Tooltip, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import type { Theme } from '@mui/material/styles';

type ArtifactStateBadgeProps = {
	label: string;
	color: string;
	tooltip: React.ReactNode;
	selected?: boolean;
	onClick?: (event: React.MouseEvent<HTMLElement>) => void;
	ariaLabel?: string;
	variant?: 'badge' | 'chip';
	loading?: boolean;
};

const badgeSx = (selected: boolean) => (theme: Theme) => ({
	display: 'inline-flex',
	alignItems: 'center',
	gap: 0.5,
	minWidth: 0,
	px: 0.65,
	py: 0.2,
	borderRadius: 1,
	border: '1px solid',
	borderColor: selected ? 'primary.main' : 'transparent',
	backgroundColor: selected ? alpha(theme.palette.primary.main, 0.08) : 'transparent',
});

export default function ArtifactStateBadge({ label, color, tooltip, selected = false, onClick, ariaLabel, variant = 'badge', loading = false }: ArtifactStateBadgeProps) {
	if (variant === 'chip') {
		return (
			<Tooltip title={tooltip}>
				<Chip
					label={label}
					icon={loading ? <CircularProgress size={11} thickness={5} color="inherit" disableShrink
						aria-label={`${label} in progress`} sx={{ animationDuration: '1s !important', animationIterationCount: 'infinite !important',
							'@media (prefers-reduced-motion: reduce)': { animationDuration: '1.6s !important' } }} /> : undefined}
					aria-busy={loading || undefined}
					size="small"
					variant="outlined"
					aria-label={ariaLabel}
					onClick={onClick ? (event) => { event.stopPropagation(); onClick(event); } : undefined}
					sx={{ height: 22, flexShrink: 0, borderColor: color, color, bgcolor: 'action.hover', fontSize: '0.7rem', fontWeight: 600, '& .MuiChip-label': { px: 1 }, '& .MuiChip-icon': { color: 'inherit', ml: 0.75, mr: 0 } }}
				/>
			</Tooltip>
		);
	}
	const content = (
		<Stack component="span" direction="row" alignItems="center" spacing={0.5} sx={{ minWidth: 0 }}>
			<Box component="span" sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: color, flexShrink: 0 }} />
			<Typography component="span" variant="caption" noWrap sx={{ fontSize: '0.62rem', color: 'text.secondary', maxWidth: 58 }}>
				{label}
			</Typography>
		</Stack>
	);

	return (
		<Tooltip title={tooltip}>
			{onClick ? (
				<ButtonBase
					onClick={(event) => {
						event.stopPropagation();
						onClick(event);
					}}
					aria-label={ariaLabel}
					sx={badgeSx(selected)}
				>
					{content}
				</ButtonBase>
			) : (
				<Box component="span" sx={badgeSx(selected)}>
					{content}
				</Box>
			)}
		</Tooltip>
	);
}
